import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { BrandProvider } from "@/contexts/BrandContext";
import ProtectedRoute from "@/routes/ProtectedRoute";
import ImpersonationBanner from "@/components/marketplace/ImpersonationBanner";

import HomePage from "@/pages/HomePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import AuthCallback from "@/pages/AuthCallback";
import CategoriesPage from "@/pages/CategoriesPage";
import CategoryDetailPage from "@/pages/CategoryDetailPage";
import ProviderDirectoryPage from "@/pages/ProviderDirectoryPage";
import ProviderProfilePage from "@/pages/ProviderProfilePage";
import RequestServicePage from "@/pages/RequestServicePage";
import RequestEntryPage from "@/pages/RequestEntryPage";
import AdminHomepageSettingsPage from "@/pages/admin/AdminHomepageSettingsPage";
import AdminTranslationsPage from "@/pages/admin/AdminTranslationsPage";
import { LangAlternates } from "@/components/layout/LanguageSwitch";
import { BASENAME, IS_ES, isAdminPath } from "@/lib/i18n/lang";
import AdminHomepageAnalyticsPage from "@/pages/admin/AdminHomepageAnalyticsPage";
import ServiceRequestDetailPage from "@/pages/dashboard/ServiceRequestDetailPage";
import OpportunityDetailPage from "@/pages/dashboard/OpportunityDetailPage";
import TurnoversPage from "@/pages/dashboard/TurnoversPage";
import TurnoverPlanDetailPage from "@/pages/dashboard/TurnoverPlanDetailPage";
import VacationSpecialtiesPage from "@/pages/dashboard/VacationSpecialtiesPage";
import DealsPage from "@/pages/DealsPage";
import ProviderDealsPage from "@/pages/dashboard/ProviderDealsPage";
import DealAlertsPage from "@/pages/dashboard/DealAlertsPage";
import ActiveJobPage from "@/pages/dashboard/ActiveJobPage";
import ActiveJobsListPage from "@/pages/dashboard/ActiveJobsListPage";
import AdminJobVerificationPage, { AdminJobDetailPage } from "@/pages/admin/AdminJobVerificationPage";
import AdminJobVerificationSettingsPage from "@/pages/admin/AdminJobVerificationSettingsPage";
import DataCenterLayout from "@/pages/admin/datacenter/DataCenterLayout";
import ImportWizard from "@/pages/admin/datacenter/ImportWizard";
import AIImportPage from "@/pages/admin/datacenter/AIImportPage";
import ExportPage from "@/pages/admin/datacenter/ExportPage";
import RecordsPage from "@/pages/admin/datacenter/RecordsPage";
import { TemplatesPage, MappingsPage, HistoryPage, ExportHistoryPage, ErrorsPage, AuditPage } from "@/pages/admin/datacenter/DataCenterLists";
import { QualityPage, DuplicatesPage, EnrichmentPage } from "@/pages/admin/datacenter/QualityPages";
import { SettingsPage, ClaimsPage } from "@/pages/admin/datacenter/SettingsClaims";
import ClaimBusinessPage from "@/pages/ClaimBusinessPage";
import ProviderServicesImportPage from "@/pages/dashboard/ProviderServicesImportPage";

import AdminHotDealsPage from "@/pages/admin/AdminHotDealsPage";
import AccountSettingsPage from "@/pages/AccountSettingsPage";
import ProviderOnboardingPage from "@/pages/ProviderOnboardingPage";
import ServiceDirectoryPage from "@/pages/ServiceDirectoryPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import BookLocalPage from "@/pages/BookLocalPage";
import HireDigitalPage from "@/pages/HireDigitalPage";
import LocationPage from "@/pages/LocationPage";
import SearchResultsPage from "@/pages/SearchResultsPage";
import CheckoutPreviewPage from "@/pages/CheckoutPreviewPage";
import CheckoutPage from "@/pages/CheckoutPage";
import PublicRequestsPage from "@/pages/PublicRequestsPage";
import RequestDetailPage from "@/pages/RequestDetailPage";
import DashboardHomePage from "@/pages/dashboard/DashboardHomePage";
import ServicesListPage from "@/pages/dashboard/ServicesListPage";
import ServiceWizardPage from "@/pages/dashboard/ServiceWizardPage";
import AvailabilityPage from "@/pages/dashboard/AvailabilityPage";
import BookingsListPage from "@/pages/dashboard/BookingsListPage";
import BookingWorkspacePage from "@/pages/dashboard/BookingWorkspacePage";
import OrdersListPage from "@/pages/dashboard/OrdersListPage";
import OrderWorkspacePage from "@/pages/dashboard/OrderWorkspacePage";
import CustomerRequestsListPage from "@/pages/dashboard/CustomerRequestsListPage";
import CalendarPage from "@/pages/dashboard/CalendarPage";
import RecurringListPage from "@/pages/dashboard/RecurringListPage";
import InboxPage from "@/pages/dashboard/InboxPage";
import ProviderFindWorkPage from "@/pages/dashboard/ProviderFindWorkPage";
import EarningsPage from "@/pages/dashboard/EarningsPage";
import ReceiptsPage from "@/pages/dashboard/ReceiptsPage";
import FeaturedPlacementPage from "@/pages/dashboard/FeaturedPlacementPage";
import MessagesCenterPage from "@/pages/dashboard/MessagesCenterPage";
import NotificationsPage from "@/pages/dashboard/NotificationsPage";
import NotificationPreferencesPage from "@/pages/dashboard/NotificationPreferencesPage";
import ReviewsToWritePage from "@/pages/dashboard/ReviewsToWritePage";
import DisputesListPage from "@/pages/dashboard/DisputesListPage";
import DisputeDetailPage from "@/pages/dashboard/DisputeDetailPage";
import FavoritesPage from "@/pages/dashboard/FavoritesPage";
import AdminNotificationsPage from "@/pages/admin/AdminNotificationsPage";
import AdminDisputesPage from "@/pages/admin/AdminDisputesPage";
import AdminLayout from "@/layouts/AdminLayout";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminCRUDPage from "@/pages/admin/AdminCRUDPage";
import AdminCRUDEditPage from "@/pages/admin/AdminCRUDEditPage";
import AdminCRUDCreatePage from "@/pages/admin/AdminCRUDCreatePage";
import AdminQuestionnairesPage from "@/pages/admin/AdminQuestionnairesPage";
import AdminQuestionnaireEditorPage from "@/pages/admin/AdminQuestionnaireEditorPage";
import AdminMatchingPage from "@/pages/admin/AdminMatchingPage";
import AdminServiceRequestsPage from "@/pages/admin/AdminServiceRequestsPage";
import AdminServiceRequestDetailPage from "@/pages/admin/AdminServiceRequestDetailPage";
import AdminBrandPage from "@/pages/admin/AdminBrandPage";
import AdminAuditLogsPage from "@/pages/admin/AdminAuditLogsPage";
import AdminReportsPage from "@/pages/admin/AdminReportsPage";
import AdminImportPage from "@/pages/admin/AdminImportPage";
import AdminRolesPage from "@/pages/admin/AdminRolesPage";
import AdminPaymentsPage from "@/pages/admin/AdminPaymentsPage";
import { AdminWebsitePage, AdminPageEditorPage } from "@/pages/admin/AdminWebsitePage";
import {
  AdminMediaPage, AdminNavigationPage, AdminRedirectsPage,
  AdminTemplatesPage, AdminTemplateEditorPage,
  AdminBlogPage, AdminHelpPage, AdminFaqsPage,
} from "@/pages/admin/AdminContentPages";
import PageRenderer from "@/pages/public/PageRenderer";
import { BlogListPage, BlogDetailPage, HelpCenterPage, HelpArticlePage } from "@/pages/public/ContentPages";
import AddressesPage from "@/pages/dashboard/AddressesPage";
import BusinessProfilePage from "@/pages/dashboard/BusinessProfilePage";
import WalletPage from "@/pages/dashboard/WalletPage";
import { SupportTicketsPage, SupportTicketDetailPage } from "@/pages/dashboard/SupportPage";
import PlansPage from "@/pages/dashboard/PlansPage";
import TeamPage from "@/pages/dashboard/TeamPage";
import TeamAcceptPage from "@/pages/dashboard/TeamAcceptPage";
import VerificationCenterPage from "@/pages/dashboard/VerificationCenterPage";
import PortfolioPage from "@/pages/dashboard/PortfolioPage";
import ExternalReviewsPage from "@/pages/dashboard/ExternalReviewsPage";
import ExpensesPage from "@/pages/dashboard/ExpensesPage";
import ProviderAnalyticsPage from "@/pages/dashboard/ProviderAnalyticsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import NotFoundPage from "@/pages/NotFoundPage";

function AppRouter() {
  const location = useLocation();
  if (IS_ES && isAdminPath(location.pathname)) {
    window.location.replace(location.pathname + location.search);
    return null;
  }
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
    <LangAlternates />
    <Routes>
      <Route path="/" element={<PageRenderer />} />
      <Route path="/legacy-home" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route path="/categories" element={<CategoriesPage />} />
      <Route path="/deals" element={<DealsPage />} />
      <Route path="/dashboard/deals" element={<ProtectedRoute><ProviderDealsPage /></ProtectedRoute>} />
      <Route path="/claim/:providerId" element={<ClaimBusinessPage />} />
      <Route path="/dashboard/services/import" element={<ProtectedRoute><ProviderServicesImportPage /></ProtectedRoute>} />
      <Route path="/dashboard/services/import/:id" element={<ProtectedRoute><ProviderServicesImportPage /></ProtectedRoute>} />
      <Route path="/dashboard/deal-alerts" element={<ProtectedRoute><DealAlertsPage /></ProtectedRoute>} />
      <Route path="/dashboard/jobs" element={<ProtectedRoute><ActiveJobsListPage /></ProtectedRoute>} />
      <Route path="/dashboard/jobs/:id" element={<ProtectedRoute><ActiveJobPage /></ProtectedRoute>} />
      <Route path="/categories/:slug" element={<CategoryDetailPage />} />
      <Route path="/providers" element={<ProviderDirectoryPage />} />
      <Route path="/providers/:slug" element={<ProviderProfilePage />} />

      <Route path="/services" element={<ServiceDirectoryPage />} />
      <Route path="/services/:slug" element={<ServiceDetailPage />} />
      <Route path="/book-local" element={<BookLocalPage />} />
      <Route path="/hire-digital" element={<HireDigitalPage />} />
      <Route path="/locations/:state" element={<LocationPage />} />
      <Route path="/locations/:state/:city" element={<LocationPage />} />
      <Route path="/search" element={<SearchResultsPage />} />
      <Route path="/checkout/preview" element={<CheckoutPreviewPage />} />
      <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />

      {/* Public request board + detail */}
      <Route path="/requests" element={<PublicRequestsPage />} />
      <Route path="/requests/:id" element={<RequestDetailPage />} />
      <Route path="/post-a-request" element={<LegacyRequestRedirect />} />
      <Route path="/requests/new" element={<LegacyRequestRedirect />} />
      <Route path="/request-service" element={<RequestEntryPage />} />
      <Route path="/request-service/:slug" element={<RequestEntryPage />} />
      <Route path="/request-a-service" element={<ProtectedRoute><RequestServicePage /></ProtectedRoute>} />
      <Route path="/request-a-service/:id" element={<ProtectedRoute><RequestServicePage /></ProtectedRoute>} />

      <Route path="/account/settings" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
      <Route path="/provider/onboarding" element={<ProtectedRoute><ProviderOnboardingPage /></ProtectedRoute>} />
      {/* /become-a-provider is now a DB-managed marketing page rendered by PageRenderer catch-all */}

      {/* Dashboard */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardHomePage /></ProtectedRoute>} />
      <Route path="/dashboard/services" element={<ProtectedRoute><ServicesListPage /></ProtectedRoute>} />
      <Route path="/dashboard/services/new" element={<ProtectedRoute><ServiceWizardPage /></ProtectedRoute>} />
      <Route path="/dashboard/services/:id/edit" element={<ProtectedRoute><ServiceWizardPage /></ProtectedRoute>} />
      <Route path="/dashboard/availability" element={<ProtectedRoute><AvailabilityPage /></ProtectedRoute>} />

      {/* Phase 3 dashboard */}
      <Route path="/dashboard/bookings" element={<ProtectedRoute><BookingsListPage /></ProtectedRoute>} />
      <Route path="/dashboard/bookings/:id" element={<ProtectedRoute><BookingWorkspacePage /></ProtectedRoute>} />
      <Route path="/dashboard/orders" element={<ProtectedRoute><OrdersListPage /></ProtectedRoute>} />
      <Route path="/dashboard/orders/:id" element={<ProtectedRoute><OrderWorkspacePage /></ProtectedRoute>} />
      <Route path="/dashboard/requests" element={<ProtectedRoute><CustomerRequestsListPage /></ProtectedRoute>} />
      <Route path="/dashboard/service-requests/:id" element={<ProtectedRoute><ServiceRequestDetailPage /></ProtectedRoute>} />
      <Route path="/dashboard/opportunities" element={<Navigate to="/dashboard/find-work" replace />} />
      <Route path="/dashboard/opportunities/:id" element={<ProtectedRoute><OpportunityDetailPage /></ProtectedRoute>} />
      <Route path="/dashboard/turnovers" element={<ProtectedRoute><TurnoversPage /></ProtectedRoute>} />
      <Route path="/dashboard/turnovers/:id" element={<ProtectedRoute><TurnoverPlanDetailPage /></ProtectedRoute>} />
      <Route path="/dashboard/vacation-specialties" element={<ProtectedRoute><VacationSpecialtiesPage /></ProtectedRoute>} />
      <Route path="/dashboard/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/dashboard/recurring" element={<ProtectedRoute><RecurringListPage /></ProtectedRoute>} />
      <Route path="/dashboard/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
      <Route path="/dashboard/find-work" element={<ProtectedRoute><ProviderFindWorkPage /></ProtectedRoute>} />

      {/* Phase 4 — payments, earnings, featured */}
      <Route path="/payment/success" element={<PaymentSuccessPage />} />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      <Route path="/dashboard/receipts" element={<ProtectedRoute><ReceiptsPage /></ProtectedRoute>} />
      <Route path="/dashboard/earnings" element={<ProtectedRoute><EarningsPage /></ProtectedRoute>} />
      <Route path="/dashboard/featured" element={<ProtectedRoute><FeaturedPlacementPage /></ProtectedRoute>} />

      {/* Phase 5 — messages, notifications, reviews, disputes, favorites */}
      <Route path="/dashboard/messages" element={<ProtectedRoute><MessagesCenterPage /></ProtectedRoute>} />
      <Route path="/dashboard/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/dashboard/notifications/preferences" element={<ProtectedRoute><NotificationPreferencesPage /></ProtectedRoute>} />
      <Route path="/dashboard/reviews" element={<ProtectedRoute><ReviewsToWritePage /></ProtectedRoute>} />
      <Route path="/dashboard/disputes" element={<ProtectedRoute><DisputesListPage /></ProtectedRoute>} />
      <Route path="/dashboard/disputes/:id" element={<ProtectedRoute><DisputeDetailPage /></ProtectedRoute>} />
      <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />

      {/* Phase 6+7 — dashboard extras */}
      <Route path="/dashboard/addresses" element={<ProtectedRoute><AddressesPage /></ProtectedRoute>} />
      <Route path="/dashboard/business-profile" element={<ProtectedRoute><BusinessProfilePage /></ProtectedRoute>} />
      <Route path="/dashboard/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/dashboard/support" element={<ProtectedRoute><SupportTicketsPage /></ProtectedRoute>} />
      <Route path="/dashboard/support/:id" element={<ProtectedRoute><SupportTicketDetailPage /></ProtectedRoute>} />
      <Route path="/dashboard/plans" element={<ProtectedRoute><PlansPage /></ProtectedRoute>} />
      <Route path="/dashboard/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/team/accept" element={<ProtectedRoute><TeamAcceptPage /></ProtectedRoute>} />
      <Route path="/dashboard/verification" element={<ProtectedRoute><VerificationCenterPage /></ProtectedRoute>} />
      <Route path="/dashboard/portfolio" element={<ProtectedRoute><PortfolioPage /></ProtectedRoute>} />
      <Route path="/dashboard/external-reviews" element={<ProtectedRoute><ExternalReviewsPage /></ProtectedRoute>} />
      <Route path="/dashboard/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
      <Route path="/dashboard/analytics" element={<ProtectedRoute><ProviderAnalyticsPage /></ProtectedRoute>} />

      {/* Admin console */}
      <Route path="/admin/notifications" element={<ProtectedRoute><AdminNotificationsPage /></ProtectedRoute>} />
      <Route path="/admin/disputes" element={<ProtectedRoute><AdminDisputesPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboardPage />} />
        <Route path="reports" element={<AdminReportsPage />} />
        <Route path="questionnaires" element={<AdminQuestionnairesPage />} />
        <Route path="questionnaires/:key" element={<AdminQuestionnaireEditorPage />} />
        <Route path="matching" element={<AdminMatchingPage />} />
        <Route path="data-center" element={<DataCenterLayout />}>
          <Route index element={<Navigate to="import" replace />} />
          <Route path="import" element={<ImportWizard />} />
          <Route path="import/:id" element={<ImportWizard />} />
          <Route path="history/:id" element={<ImportWizard />} />
          <Route path="ai-import" element={<AIImportPage />} />
          <Route path="ai-import/:id" element={<AIImportPage />} />
          <Route path="export" element={<ExportPage />} />
          <Route path="templates" element={<TemplatesPage />} />
          <Route path="mappings" element={<MappingsPage />} />
          <Route path="history" element={<HistoryPage />} />
          <Route path="exports" element={<ExportHistoryPage />} />
          <Route path="errors" element={<ErrorsPage />} />
          <Route path="duplicates" element={<DuplicatesPage />} />
          <Route path="enrichment" element={<EnrichmentPage />} />
          <Route path="quality" element={<QualityPage />} />
          <Route path="records" element={<RecordsPage />} />
          <Route path="claims" element={<ClaimsPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
        <Route path="hot-deals" element={<AdminHotDealsPage />} />
        <Route path="job-verification" element={<AdminJobVerificationPage />} />
        <Route path="job-verification/settings" element={<AdminJobVerificationSettingsPage />} />
        <Route path="job-verification/:id" element={<AdminJobDetailPage />} />
        <Route path="service-requests" element={<AdminServiceRequestsPage />} />
        <Route path="service-requests/:id" element={<AdminServiceRequestDetailPage />} />
        <Route path="audit-logs" element={<AdminAuditLogsPage />} />
        <Route path="brand" element={<AdminBrandPage />} />
        <Route path="settings" element={<AdminBrandPage />} />
        <Route path="import" element={<AdminImportPage />} />
        <Route path="roles" element={<AdminRolesPage />} />
        <Route path="payments" element={<AdminPaymentsPage />} />
        <Route path="website" element={<AdminWebsitePage />} />
        <Route path="website/:pageId" element={<AdminPageEditorPage />} />
        <Route path="media" element={<AdminMediaPage />} />
        <Route path="navigation" element={<AdminNavigationPage />} />
        <Route path="redirects" element={<AdminRedirectsPage />} />
        <Route path="translations" element={<AdminTranslationsPage />} />
        <Route path="templates" element={<AdminTemplatesPage />} />
        <Route path="templates/:tid" element={<AdminTemplateEditorPage />} />
        <Route path="blog" element={<AdminBlogPage />} />
        <Route path="help" element={<AdminHelpPage />} />
        <Route path="faqs" element={<AdminFaqsPage />} />
        <Route path="entities/:entity" element={<AdminCRUDPage />} />
        <Route path="entities/:entity/new" element={<AdminCRUDCreatePage />} />
        <Route path="website/homepage" element={<AdminHomepageSettingsPage />} />
        <Route path="analytics/homepage" element={<AdminHomepageAnalyticsPage />} />
        <Route path="entities/:entity/:id" element={<AdminCRUDEditPage />} />
      </Route>

      {/* Phase 8 — public content + dynamic page renderer (catch-all last) */}
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogDetailPage />} />
      <Route path="/help" element={<HelpCenterPage />} />
      <Route path="/help/:slug" element={<HelpArticlePage />} />
      <Route path="*" element={<PageRenderer />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter basename={BASENAME}>
          <BrandProvider>
            <AuthProvider>
              <ImpersonationBanner />
              <AppRouter />
              <Toaster position="top-right" richColors closeButton />
            </AuthProvider>
          </BrandProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

function LegacyRequestRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/request-a-service${search}`} replace />;
}
